// Optional jersey entry v2. Preview is pure; only the guarded server issues identities.
import {
  jerseyMatchKey,
  jerseyPeriodToken,
  type JerseyIdentifierConfig,
  type JerseySkuKind,
  type JerseyToken,
} from './jersey-identifiers';

export type JerseyOutputMode = 'auto' | 'manual';
export type JerseyOutputIntent = { mode: JerseyOutputMode; manual_value?: string | null };
export const JERSEY_NAME_PARTS = [
  'team',
  'period',
  'collection',
  'kit',
  'sleeve',
  'collar',
  'edition',
  'fit',
  'audience',
  'print',
  'custom',
] as const;
export type JerseyNamePart = (typeof JERSEY_NAME_PARTS)[number];
export interface JerseyEntryConfig {
  version: 2;
  enabled: boolean;
  neutral_prefix: string;
  missing_token: string;
  name_order: JerseyNamePart[];
  separator: string;
  size_order: string[];
}
export const defaultJerseyEntryConfig = (): JerseyEntryConfig => ({
  version: 2,
  enabled: false,
  neutral_prefix: 'JER',
  missing_token: 'UNK',
  name_order: [...JERSEY_NAME_PARTS],
  separator: ' | ',
  size_order: [],
});
export interface JerseyDetailsV2 {
  team?: {
    id?: string | null;
    entered_name?: string | null;
    classification?: 'club' | 'country' | null;
  } | null;
  kit_key?: string | null;
  kit_other_label?: string | null;
  period?: { kind: 'calendar_year' | 'season'; value: string } | null;
  collection_key?: string | null;
  sleeve_key?: string | null;
  collar_key?: string | null;
  print_mode?: 'no_name' | 'named' | 'customisable' | null;
  player_name?: string | null;
  shirt_number?: string | null;
  edition_key?: string | null;
  fit_key?: string | null;
  audience_key?: string | null;
  custom_design_text?: string | null;
}
export interface JerseyEntryContext {
  contract_version: 2;
  enabled: boolean;
  configuration_version_id: string;
  recipe_version: 2;
  entry_config: JerseyEntryConfig;
  identifier_config: JerseyIdentifierConfig | null;
  teams: { id: string; name: string; code: string }[];
  current: { details?: JerseyDetailsV2; name_mode?: JerseyOutputMode } | null;
  occupied_variants: { id: string; size: string; color: string | null; is_active: boolean }[];
  next_offset: number | null;
}
export const JERSEY_HELPER_CODES = new Set([
  'TEAM',
  'KIT',
  'EDITION',
  'PERIOD',
  'YEAR',
  'SEASON',
  'COLLECTION',
  'SLEEVE_TYPE',
  'COLLAR_TYPE',
  'PLAYER_NAME',
  'PLAYER_NUMBER',
  'PRINT_MODE',
  'FIT',
  'AUDIENCE',
]);
export const cleanJerseyText = (value?: string | null) => (value ?? '').trim().replace(/\s+/g, ' ');

export function jerseyEntryIssues(details: JerseyDetailsV2): Record<string, string> {
  const issues: Record<string, string> = {};
  const bounded = (key: string, value: string | null | undefined, max: number) => {
    if (
      value != null &&
      (typeof value !== 'string' ||
        cleanJerseyText(value).length > max ||
        /[\u0000-\u001f\u007f]/.test(value))
    )
      issues[key] = `Use text of ${max} characters or fewer without control characters.`;
  };
  bounded('team', details.team?.entered_name, 100);
  bounded('player_name', details.player_name, 100);
  bounded('custom_design_text', details.custom_design_text, 200);
  bounded('kit_other_label', details.kit_other_label, 200);
  for (const key of [
    'kit_key',
    'collection_key',
    'sleeve_key',
    'collar_key',
    'edition_key',
    'fit_key',
    'audience_key',
  ] as const)
    bounded(key, details[key], 100);
  if (details.period) {
    try {
      jerseyPeriodToken(details.period);
    } catch {
      issues.period = 'Use a year such as 2015 or a consecutive season such as 2015/16.';
    }
  }
  if (details.shirt_number && !/^[0-9]{1,3}$/.test(details.shirt_number))
    issues.shirt_number = 'Use 1–3 digits. Leading zeros are kept.';
  if (details.print_mode && !['no_name', 'named', 'customisable'].includes(details.print_mode))
    issues.print_mode = 'Choose a valid print option.';
  if (
    details.print_mode &&
    details.print_mode !== 'named' &&
    (details.player_name || details.shirt_number)
  )
    issues.print_mode = 'Keep Named print or clear the player details before changing print mode.';
  return issues;
}

export function jerseyEntryToken(
  config: JerseyIdentifierConfig | null | undefined,
  kind: JerseySkuKind,
  value?: string | null,
): JerseyToken | undefined {
  const key = jerseyMatchKey(value ?? '');
  if (!key) return undefined;
  const matches = (config?.tokens[kind] ?? []).filter(
    (t) =>
      t.active !== false &&
      [t.key, t.label, t.token, ...t.aliases].some((alias) => jerseyMatchKey(alias) === key),
  );
  return matches.length === 1 ? matches[0] : undefined;
}

export function jerseyNameSuggestion(
  details: JerseyDetailsV2,
  context: Pick<JerseyEntryContext, 'entry_config' | 'identifier_config' | 'teams'>,
): string | null {
  const label = (kind: JerseySkuKind, key?: string | null) =>
    key?.toUpperCase() === 'UNSPECIFIED'
      ? ''
      : jerseyEntryToken(context.identifier_config, kind, key)?.label || cleanJerseyText(key);
  const parts: Record<JerseyNamePart, string> = {
    team:
      cleanJerseyText(details.team?.entered_name) ||
      context.teams.find((t) => t.id === details.team?.id)?.name ||
      '',
    period: details.period?.value || '',
    collection: label('collection', details.collection_key),
    kit: cleanJerseyText(details.kit_other_label) || label('kit', details.kit_key),
    sleeve: label('sleeve', details.sleeve_key),
    collar: label('collar', details.collar_key),
    edition: label('edition', details.edition_key),
    fit: label('fit', details.fit_key),
    audience: label('audience', details.audience_key),
    print:
      [cleanJerseyText(details.player_name), details.shirt_number || '']
        .filter(Boolean)
        .join(' ') ||
      (details.print_mode === 'no_name'
        ? 'No Name'
        : details.print_mode === 'customisable'
          ? 'Customisable'
          : ''),
    custom: cleanJerseyText(details.custom_design_text),
  };
  const values = context.entry_config.name_order.map((k) => parts[k]).filter(Boolean);
  if (!values.length) return null;
  if (!values.some((v) => /\bjersey\b/i.test(v))) values.push('Jersey');
  // Keep the current display format even for published recipes with the old separator.
  return values.join(' | ').toUpperCase();
}

export function jerseyEntryPrefix(
  details: JerseyDetailsV2,
  context: Pick<JerseyEntryContext, 'entry_config' | 'identifier_config' | 'teams'>,
): string {
  const mapping = context.identifier_config?.teams.find((t) => t.team_id === details.team?.id);
  return mapping?.code || context.entry_config.neutral_prefix;
}

export function jerseyEntryPrintToken(
  details: JerseyDetailsV2,
  config?: JerseyIdentifierConfig | null,
): JerseyToken | undefined {
  return (config?.tokens.print ?? []).find(
    (t) =>
      t.active !== false &&
      (details.print_mode === 'no_name'
        ? t.print_kind === 'no_name'
        : details.print_mode === 'customisable'
          ? t.print_kind === 'customisable'
          : (details.player_name || details.shirt_number) &&
            t.print_kind === 'named_player' &&
            cleanJerseyText(t.player_name) === cleanJerseyText(details.player_name) &&
            (t.shirt_number ?? '') === (details.shirt_number ?? '')),
  );
}

export function jerseyEntrySku(
  details: JerseyDetailsV2,
  code: string,
  colour: string,
  size: string,
  context: Pick<JerseyEntryContext, 'entry_config' | 'identifier_config'>,
): string {
  const missing = context.entry_config.missing_token;
  const token = (kind: JerseySkuKind, key?: string | null) =>
    jerseyEntryToken(context.identifier_config, kind, key)?.token || missing;
  const print = jerseyEntryPrintToken(details, context.identifier_config);
  const variantToken = (kind: 'colour' | 'size', value: string) =>
    jerseyEntryToken(context.identifier_config, kind, value)?.token ||
    (jerseyMatchKey(value)
      ? 'X' +
        Array.from(jerseyMatchKey(value), (ch) =>
          ch.codePointAt(0)!.toString(16).padStart(6, '0'),
        ).join('')
      : missing);
  return [
    code,
    details.period ? jerseyPeriodToken(details.period) : missing,
    token('collection', details.collection_key),
    token('kit', details.kit_key),
    token('sleeve', details.sleeve_key),
    print?.token || missing,
    token('edition', details.edition_key),
    token('fit', details.fit_key),
    token('collar', details.collar_key),
    token('audience', details.audience_key),
    variantToken('colour', colour),
    variantToken('size', size),
  ].join('-');
}

export function jerseyCombination(
  colour: string | null,
  size: string,
  config?: JerseyIdentifierConfig | null,
): string {
  return [
    jerseyEntryToken(config, 'colour', colour)?.key || jerseyMatchKey(colour || ''),
    jerseyEntryToken(config, 'size', size)?.key || jerseyMatchKey(size),
  ].join('\u001f');
}
export function nextUnusedJerseySize(
  source: { size: string; color: string | null },
  order: string[],
  occupied: { size: string; color: string | null }[],
  config?: JerseyIdentifierConfig | null,
): string | null {
  const currentKey = jerseyCombination(source.color, source.size, config);
  const at = order.findIndex(
    (size) => jerseyCombination(source.color, size, config) === currentKey,
  );
  if (at < 0) return null;
  const used = new Set(occupied.map((v) => jerseyCombination(v.color, v.size, config)));
  return (
    order.slice(at + 1).find((size) => !used.has(jerseyCombination(source.color, size, config))) ??
    null
  );
}
